/**
 * EstimatedResponsibility â€” upcoming-visit precollect section.
 *
 * Renders only in the precollect flow (when the URL contains
 * `:appointmentId`). Hidden in balance-pay where there's no upcoming visit.
 *
 * **Phase 0.7.0+ behavior:** the patient cannot pay a partial estimate.
 * They pay either the full remaining estimate or nothing. Two reasons
 * (per `Patient-Payment-Portal-Backend-v0.7.0` handoff):
 *
 *   1. Stranded sub-Stripe-minimum remainders â€” a partial pay could leave
 *      $0.30 owed, which Stripe can't charge.
 *   2. Front-desk discretion is the right escalation path if the patient
 *      genuinely can't pay the full estimate.
 *
 * **v0.7.0 â€” display fields rebuild.** Per QA Section A.2 (and Backend
 * v0.7.1's confirmation that 7 new fields are live on /checkin/balance):
 *
 *   - Identity line (provider Â· facility Â· appointment-type-name) below
 *     the section title. `appointment_type_name` is null until CRM grants
 *     APPOINTMENT_TYPE lookup access (per Backend v0.7.1 reply Item 4) â€”
 *     null fields are omitted from the line cleanly.
 *   - Breakdown block (deductible / copay / coinsurance / insurance-pay)
 *     between the header and the action row. Coinsurance is raw varchar
 *     ("20%", "$30") â€” displayed as-is per backend's "we don't parse,
 *     you don't parse" instruction. Zero values omitted to reduce noise.
 *   - Info ("i") tooltip next to the section title per QA BUG-004,
 *     explaining what "Estimated Responsibility" means.
 *
 * Three states unchanged:
 *
 *   A. `estimate_remaining === 0` â€” section is hidden (PayPage skips us).
 *   B. `0 < estimate_remaining < 50` cents â€” un-actionable info card.
 *   C. `estimate_remaining >= 50` cents â€” checkbox + summary, pre-checked.
 */
import { useEffect, useId } from 'react';
import { Info } from 'lucide-react';

import { Alert } from '@tensaw/design-system/feedback';
import { Card, CardContent } from '@tensaw/design-system/layout';
import { Checkbox, Label } from '@tensaw/design-system/primitives';

import type { FetchCheckinBalanceResponse } from '../../api/types';
import { format } from '../../lib/money';

/**
 * Stripe's minimum chargeable amount in USD, in cents. Set client-side
 * to gate the UI before the backend rejects with AMOUNT_MISMATCH; the
 * backend enforces the same floor regardless.
 */
export const STRIPE_USD_MIN_CENTS = 50;

const ESTIMATE_TOOLTIP_COPY =
  "Your share of the visit cost based on your insurance benefits. " +
  "Includes any deductible, copay, and coinsurance the clinic estimates " +
  "you'll owe. Final amount may change after your insurance processes " +
  "the claim.";

interface EstimatedResponsibilityProps {
  data: FetchCheckinBalanceResponse;
  /** True when the patient has elected to include the full estimate in the charge. */
  selected: boolean;
  onSelectedChange: (next: boolean) => void;
}

/**
 * Coinsurance is a raw varchar from the CRM (`"20%"`, `"$30"`, `"0.0"`,
 * `"20% of allowed"`, `""`, or null). Backend deliberately doesn't parse
 * it and explicitly tells FE not to parse either â€” display as-is. But
 * v0.8.3 QA flagged that rendering `0.0` literally looks broken: it has
 * no `%` or `$` to ground it, and from a patient's perspective "what's
 * 0.0?" is a fair question.
 *
 * Strategy: suppress the row entirely when the value parses to numeric
 * zero. Does NOT reformat non-zero values â€” `"$30"` still renders as
 * `"$30"`, `"20% of allowed"` still renders verbatim. Only the
 * meaningless-zero case is suppressed.
 *
 * Returns true for: `null`, `""`, `"0"`, `"0.0"`, `"0.00"`, `"0%"`,
 * `"$0"`, `"$0.00"`, `"  0  "`. Returns false for any value that
 * contains a non-zero digit or non-numeric text content.
 */
function isCoinsuranceZeroOrEmpty(raw: string | null): boolean {
  if (raw === null) return true;
  const trimmed = raw.trim();
  if (trimmed === '') return true;
  const stripped = trimmed.replace(/[$%\s]/g, '');
  const asNumber = Number(stripped);
  if (Number.isNaN(asNumber)) return false; // non-numeric content â€” render verbatim
  return asNumber === 0;
}

export function EstimatedResponsibility({
  data,
  selected,
  onSelectedChange,
}: EstimatedResponsibilityProps) {
  const checkboxId = useId();
  const tooltipId = useId();
  const remaining = data.estimate_remaining_minor_units;
  const paid = data.estimate_paid_minor_units;
  const total = data.estimate_total_minor_units;
  const subMinimum = remaining > 0 && remaining < STRIPE_USD_MIN_CENTS;

  // If the remaining is sub-Stripe-minimum, force selected=false so the
  // patient can't accidentally try to pay it. Defensive: PayPage's
  // buildRequest also rejects this, but a stale `selected: true` from a
  // prior estimate state could leak through if the value changed
  // mid-session (e.g. front-desk applied a payment).
  useEffect(() => {
    if (subMinimum && selected) onSelectedChange(false);
  }, [subMinimum, selected, onSelectedChange]);

  const identityLine = formatIdentityLine(data);
  const coinsuranceIsZero = isCoinsuranceZeroOrEmpty(data.coinsurance);
  const hasBreakdown =
    data.deductible_minor_units > 0 ||
    data.copay_minor_units > 0 ||
    !coinsuranceIsZero ||
    data.insurance_estimated_pay_minor_units > 0;

  return (
    <Card>
      <CardContent className="p-0">
        {/* Header â€” identity, total, paid */}
        <div className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
          <div className="flex items-start gap-3">
            {!subMinimum && (
              <Checkbox
                id={checkboxId}
                checked={selected}
                onCheckedChange={(next) => { onSelectedChange(Boolean(next)); }}
                aria-label="Pay full estimated visit cost"
                className="mt-1"
              />
            )}
            <div>
              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor={subMinimum ? undefined : checkboxId}
                  className="text-base font-semibold text-foreground"
                >
                  Estimated Responsibility
                </Label>
                {/* Per QA BUG-004: info button explaining what this
                    section means. Native title attribute is the
                    cheapest accessible tooltip â€” design-system has no
                    Tooltip primitive (would mean upstream work for
                    one micro-interaction). aria-describedby attaches
                    the same copy for screen readers. */}
                <button
                  type="button"
                  aria-label="About estimated responsibility"
                  aria-describedby={tooltipId}
                  title={ESTIMATE_TOOLTIP_COPY}
                  className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
                <span id={tooltipId} className="sr-only">
                  {ESTIMATE_TOOLTIP_COPY}
                </span>
              </div>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {/*
                  v0.8.4 / backend v0.8.2: render the pre-formatted
                  clinic-local string verbatim. DO NOT pass through
                  Intl.DateTimeFormat or `new Date()` â€” the browser's
                  timezone is irrelevant, what matters is the clinic's
                  local time, which only the backend knows.
                  Per ledger #49.
                */}
                Upcoming visit. {data.appointment_date_formatted}
              </p>
              {identityLine && (
                <p className="mt-0.5 whitespace-pre-line text-xs text-muted-foreground leading-relaxed">
                  {identityLine}
                </p>
              )}
            </div>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            <div>
              Total estimate{' '}
              <span className="font-semibold text-primary tabular-nums">
                {format(total)}
              </span>
            </div>
            {paid > 0 && (
              <div className="mt-0.5 text-[11px] text-muted-foreground tabular-nums">
                Already paid {format(paid)}
              </div>
            )}
          </div>
        </div>

        {/* Breakdown block â€” only when at least one breakdown field is
            populated. Lets the patient see what makes up the estimate. */}
        {hasBreakdown && (
          <BreakdownBlock data={data} />
        )}

        {/* Action area */}
        <div className="px-6 py-5">
          {subMinimum ? (
            <SubMinimumNotice remainingCents={remaining} />
          ) : (
            <CheckboxRowSummary remainingCents={remaining} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Estimate breakdown â€” Deductible, Copay, Coinsurance, Insurance Pay.
 *
 * Each row omitted when its value is empty per the testing-team A.2 spec
 * ("If estimated insurance pay is $0, just omit that line"). Coinsurance
 * is raw varchar per Backend v0.7.1 Item 4 â€” displayed verbatim, no parse.
 */
function BreakdownBlock({ data }: { data: FetchCheckinBalanceResponse }) {
  const rows: { label: string; value: string }[] = [];
  if (data.deductible_minor_units > 0) {
    rows.push({ label: 'Deductible', value: format(data.deductible_minor_units) });
  }
  if (data.copay_minor_units > 0) {
    rows.push({ label: 'Copay', value: format(data.copay_minor_units) });
  }
  if (!isCoinsuranceZeroOrEmpty(data.coinsurance)) {
    rows.push({ label: 'Coinsurance', value: `${data.coinsurance}%` as string });
  }
  if (data.insurance_estimated_pay_minor_units > 0) {
    rows.push({
      label: 'Insurance estimated to pay',
      value: format(data.insurance_estimated_pay_minor_units),
    });
  }

  if (rows.length === 0) return null;

  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-2 border-b border-border bg-muted/20 px-6 py-4 text-sm sm:grid-cols-2">
      {rows.map((r) => (
        <div key={r.label} className="flex items-baseline justify-between gap-2 sm:justify-between sm:gap-3">
          <dt className="text-muted-foreground">{r.label}</dt>
          <dd className="font-medium text-foreground tabular-nums">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/**
 * Canonical case: shows the remaining amount and a brief explanation. The
 * checkbox itself lives in the header â€” this is the body of the card.
 */
function CheckboxRowSummary({ remainingCents }: { remainingCents: number }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <p className="text-sm text-muted-foreground">
        Pay the full estimated cost now to speed up check-in. You can also
        uncheck this to pay only your past balance.
      </p>
      <div className="text-right">
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
          Pay today
        </div>
        <div className="text-base font-semibold text-foreground tabular-nums">
          {format(remainingCents)}
        </div>
      </div>
    </div>
  );
}

/**
 * Sub-$0.50 case: the remaining is real but below Stripe's minimum charge.
 * The patient can't settle it online â€” they'll pay at the clinic. Surface
 * it as an Alert rather than a checkbox so it's not interactable.
 */
function SubMinimumNotice({ remainingCents }: { remainingCents: number }) {
  return (
    <Alert variant="info" title={`Remaining ${format(remainingCents)} â€” please pay at the clinic`}>
      Amounts under {format(STRIPE_USD_MIN_CENTS)} can&rsquo;t be charged
      online. Please bring exact change or pay at the front desk when you
      arrive.
    </Alert>
  );
}

/**
 * Compose the visit-identity line below the section title:
 *   provider Â· facility Â· appointment-type-name
 *
 * `appointment_type_name` is currently always null per Backend v0.7.1
 * (CRM lookup gap) â€” when it eventually populates, no FE change needed,
 * it just slots in. Null fields drop out cleanly via `.filter(Boolean)`.
 */
function formatIdentityLine(d: FetchCheckinBalanceResponse): string | null {
  const parts = [d.provider_name, d.facility_name, d.appointment_type_name]
    .filter(Boolean) as string[];
  if (parts.length === 0) return null;
  return parts.join('\n');
}

// v0.8.4: `formatDateTime` helper removed. Pre-formatting now happens
// backend-side; FE renders `appointment_date_formatted` verbatim. Per
// backend v0.8.2 contract + FE ledger #49.
